import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { events } from '../../mocks/events';
import { useSEO } from '../../utils/seo';
import AddToCalendarButton from '../../components/feature/AddToCalendarButton';
import ShareEventButton from '../../components/feature/ShareEventButton';
import RsvpButton from '../../components/feature/RsvpButton';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function EventsPage() {
  useSEO({
    title: 'Roseville CA Events Calendar - Local Events, Festivals & Community Activities',
    description: 'Find events in Roseville California. Browse local festivals, concerts, farmers markets, community events, and activities happening in Roseville CA. Updated events calendar with dates and details.',
    keywords: 'Roseville CA events, events in Roseville California, Roseville festivals, Roseville concerts, farmers market Roseville, community events Roseville, things to do this weekend Roseville',
    canonical: '/events',
    ogImage: 'https://public.readdy.ai/ai/img_res/72ecf0e4f135ba365af725801f6677c7.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville Events Calendar',
          description: 'Complete calendar of events, festivals, and activities in Roseville, California',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/events`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/` },
            { '@type': 'ListItem', position: 2, name: 'Events', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/events` },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 cursor-pointer">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">Roseville.org</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Home</Link>
              <Link to="/recreation" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Recreation</Link>
              <Link to="/real-estate" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Real Estate</Link>
              <Link to="/utilities" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Utilities</Link>
              <Link to="/events" className="text-teal-600 font-semibold cursor-pointer">Events</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-amber-500 to-amber-700">
        <div className="max-w-7xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl font-bold mb-4">Upcoming Events</h1>
          <p className="text-xl text-amber-100 max-w-2xl mx-auto mb-6">
            Discover festivals, concerts, and community gatherings happening in Roseville
          </p>
          <Link
            to="/calendar"
            className="inline-flex items-center space-x-2 bg-white text-amber-700 px-6 py-3 rounded-full font-semibold hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-calendar-line text-lg"></i>
            <span>View Events Calendar</span>
          </Link>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event) => (
              <div
                key={event.id}
                className="group"
              >
                <Link to={`/events/${event.id}`} className="cursor-pointer block">
                  <div className="relative rounded-2xl overflow-hidden shadow-lg mb-4 h-72">
                    <img 
                      src={event.image} 
                      alt={event.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-white rounded-lg px-4 py-3 text-center shadow-lg">
                      <div className="text-teal-600 font-bold text-xs uppercase">{event.month}</div>
                      <div className="text-gray-900 font-bold text-2xl">{event.day}</div>
                    </div>
                    <div className="absolute top-4 right-4 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <RsvpButton eventId={event.id} variant="icon" />
                      <ShareEventButton event={event} variant="icon" />
                      <AddToCalendarButton event={event} variant="icon" />
                    </div>
                  </div>
                </Link>
                
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {event.category}
                    </span>
                    <div className="flex items-center space-x-2">
                      <RsvpButton eventId={event.id} variant="compact" />
                      <ShareEventButton event={event} variant="compact" />
                      <AddToCalendarButton event={event} variant="compact" />
                    </div>
                  </div>
                  <Link to={`/events/${event.id}`} className="cursor-pointer block">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {event.title}
                    </h3>
                    <div className="flex items-center text-gray-600 text-sm space-x-4">
                      <div className="flex items-center space-x-1">
                        <i className="ri-map-pin-line text-base"></i>
                        <span>{event.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <i className="ri-time-line text-base"></i>
                        <span>{event.time}</span>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <MailchimpNewsletter />

      {/* Source Attribution */}
      <section className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="border-t border-gray-100 pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-database-2-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Event information sourced from <strong className="text-gray-700">City of Roseville Parks &amp; Recreation</strong>, <strong className="text-gray-700">Downtown Roseville Partnership</strong>, <strong className="text-gray-700">Placer County Fairgrounds</strong>, and verified community event submissions.
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    Data last updated: March 20, 2025
                  </p>
                </div>
              </div>
              <a href="/data-sources" className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer transition-colors flex-shrink-0">
                View All Sources <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">Roseville.org</span>
            </div>
            <p className="text-gray-600 text-sm">
              © 2025 Roseville.org. All rights reserved. | <a href="https://readdy.ai/?ref=logo" className="text-teal-600 hover:underline cursor-pointer">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
